(() => {
  if (customElements.get('hy-product-reels')) return;

  class HyProductReels extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.cards = Array.from(this.querySelectorAll('[data-hy-reel-card]'));
      this.track = this.querySelector('.hy-product-reels__track');
      this.modal = this.querySelector('[data-hy-reel-modal]');
      this.autoplay = this.dataset.autoplay === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.saveData = Boolean(navigator.connection && navigator.connection.saveData);
      this.canAutoplay = this.autoplay && !this.reducedMotion && !this.saveData;
      this.modalOpen = false;
      this.dragMoved = false;
      this.cardsToResumeAfterModal = new Set();

      this.cards.forEach((card, index) => this.setupCard(card, index));
      this.setupTrackDrag();
      this.setupObserver();
      this.setupModal();

      document.addEventListener(
        'visibilitychange',
        () => {
          if (!document.hidden) return;
          this.cards.forEach((card) => this.pauseCard(card));
          this.pauseModalMedia();
        },
        { signal: this.signal },
      );
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.abortController) this.abortController.abort();

      if (this.modalOpen) this.closeModal(false);
      if (this.modal && this.modal.parentNode === document.body) this.modal.remove();

      this.initialized = false;
    }

    setupCard(card, index) {
      const video = card.querySelector('video');
      const openButton = card.querySelector('[data-hy-reel-open]');

      if (video) video.muted = true;

      if (openButton) {
        openButton.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (this.dragMoved) return;
            this.openModal(index, openButton);
          },
          { signal: this.signal },
        );
      }

      card.addEventListener(
        'click',
        () => {
          if (this.dragMoved) return;
          this.openModal(index, openButton || card);
        },
        { signal: this.signal },
      );
    }

    setupTrackDrag() {
      if (!this.track) return;

      let dragging = false;
      let pointerCaptured = false;
      let startX = 0;
      let startScrollLeft = 0;

      this.track.addEventListener(
        'pointerdown',
        (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;

          dragging = true;
          pointerCaptured = false;
          this.dragMoved = false;
          startX = event.clientX;
          startScrollLeft = this.track.scrollLeft;
        },
        { signal: this.signal },
      );

      this.track.addEventListener(
        'pointermove',
        (event) => {
          if (!dragging) return;

          const distance = event.clientX - startX;
          if (Math.abs(distance) > 5 && !this.dragMoved) {
            this.dragMoved = true;
            this.track.classList.add('is-dragging');

            try {
              this.track.setPointerCapture(event.pointerId);
              pointerCaptured = true;
            } catch (error) {
              // Pointer capture is an enhancement; dragging still works without it.
            }
          }

          this.track.scrollLeft = startScrollLeft - distance;

          if (this.dragMoved) event.preventDefault();
        },
        { signal: this.signal },
      );

      const stopDragging = (event) => {
        if (!dragging) return;
        dragging = false;
        this.track.classList.remove('is-dragging');

        if (pointerCaptured) {
          try {
            this.track.releasePointerCapture(event.pointerId);
          } catch (error) {
            // The pointer may already have been released by the browser.
          }
        }

        window.setTimeout(() => {
          this.dragMoved = false;
        }, 0);
      };

      this.track.addEventListener('pointerup', stopDragging, { signal: this.signal });
      this.track.addEventListener('pointercancel', stopDragging, { signal: this.signal });
    }

    setupObserver() {
      if (!('IntersectionObserver' in window)) {
        this.cards.forEach((card) => {
          this.loadTikTok(card);
          if (this.canAutoplay) this.playCard(card);
        });
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const card = entry.target;

            if (entry.isIntersecting) this.loadTikTok(card);

            if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
              if (this.canAutoplay && !this.modalOpen) this.playCard(card);
            } else if (!entry.isIntersecting || entry.intersectionRatio <= 0.15) {
              this.pauseCard(card);
            }
          });
        },
        {
          rootMargin: '160px 0px',
          threshold: [0, 0.15, 0.55],
        },
      );

      this.cards.forEach((card) => this.observer.observe(card));
    }

    setupModal() {
      if (!this.modal) return;

      document.body.appendChild(this.modal);
      this.modalDialog = this.modal.querySelector('[role="dialog"]');
      this.modalStage = this.modal.querySelector('[data-hy-reel-modal-stage]');
      this.previousButton = this.modal.querySelector('[data-hy-reel-previous]');
      this.nextButton = this.modal.querySelector('[data-hy-reel-next]');
      this.addButton = this.modal.querySelector('[data-hy-reel-add]');

      this.modal.querySelectorAll('[data-hy-reel-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeModal(), { signal: this.signal });
      });

      if (this.previousButton) {
        this.previousButton.addEventListener('click', () => this.showRelativeModalVideo(-1), {
          signal: this.signal,
        });
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.showRelativeModalVideo(1), {
          signal: this.signal,
        });
      }

      if (this.addButton) {
        this.addButton.addEventListener('click', () => this.submitProductForm(), {
          signal: this.signal,
        });
      }

      let swipeStartY = null;
      let swipeStartX = null;

      this.modalStage.addEventListener(
        'pointerdown',
        (event) => {
          if (event.pointerType === 'mouse') return;
          swipeStartY = event.clientY;
          swipeStartX = event.clientX;
        },
        { signal: this.signal },
      );

      this.modalStage.addEventListener(
        'pointerup',
        (event) => {
          if (swipeStartY === null || swipeStartX === null) return;

          const deltaY = event.clientY - swipeStartY;
          const deltaX = event.clientX - swipeStartX;
          swipeStartY = null;
          swipeStartX = null;

          if (Math.max(Math.abs(deltaY), Math.abs(deltaX)) < 52) return;

          if (Math.abs(deltaY) >= Math.abs(deltaX)) {
            this.showRelativeModalVideo(deltaY < 0 ? 1 : -1);
          } else {
            this.showRelativeModalVideo(deltaX < 0 ? 1 : -1);
          }
        },
        { signal: this.signal },
      );

      document.addEventListener(
        'keydown',
        (event) => {
          if (!this.modalOpen) return;

          if (event.key === 'Escape') {
            event.preventDefault();
            this.closeModal();
            return;
          }

          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault();
            this.showRelativeModalVideo(-1);
            return;
          }

          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault();
            this.showRelativeModalVideo(1);
            return;
          }

          if (event.key === 'Tab') this.trapModalFocus(event);
        },
        { signal: this.signal },
      );
    }

    openModal(index, opener) {
      if (!this.modal || !this.cards[index]) return;

      this.activeIndex = index;
      this.modalOpener = opener;
      this.cardsToResumeAfterModal = new Set(
        this.cards.filter((card) => {
          const video = card.querySelector('video');
          return (
            (video && !video.paused && !video.ended) ||
            (this.canAutoplay && this.isCardVisible(card, 0.55))
          );
        }),
      );
      this.modalOpen = true;
      this.cards.forEach((card) => this.pauseCard(card));
      this.modal.hidden = false;
      this.modal.classList.add('is-open');
      document.documentElement.classList.add('hy-reels-modal-open');
      this.showModalVideo(index);

      window.requestAnimationFrame(() => {
        if (this.modalDialog) this.modalDialog.focus({ preventScroll: true });
      });
    }

    closeModal(restoreFocus = true) {
      if (!this.modal) return;

      this.pauseModalMedia();
      this.modal.classList.remove('is-open');
      this.modal.hidden = true;
      this.modalOpen = false;
      document.documentElement.classList.remove('hy-reels-modal-open');
      this.resumeCardsAfterModal();

      if (restoreFocus && this.modalOpener && this.modalOpener.isConnected) {
        this.modalOpener.focus({ preventScroll: true });
      }
    }

    showRelativeModalVideo(direction) {
      if (this.cards.length < 2) return;
      const nextIndex = (this.activeIndex + direction + this.cards.length) % this.cards.length;
      this.showModalVideo(nextIndex);
    }

    showModalVideo(index) {
      if (!this.modalStage || !this.cards[index]) return;

      this.pauseModalMedia();
      this.modalStage.replaceChildren();
      this.activeIndex = index;

      const card = this.cards[index];
      const sourceVideo = card.querySelector('video');
      const sourceIframe = card.querySelector('iframe');

      if (sourceVideo) {
        const modalVideo = sourceVideo.cloneNode(true);
        modalVideo.className = 'hy-reels-modal__video';
        modalVideo.controls = true;
        modalVideo.muted = true;
        modalVideo.autoplay = true;
        modalVideo.playsInline = true;
        modalVideo.preload = 'metadata';
        modalVideo.removeAttribute('loading');
        modalVideo.setAttribute('controlslist', 'nodownload noplaybackrate');
        this.modalStage.appendChild(modalVideo);
        modalVideo.load();

        const playPromise = modalVideo.play();
        if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
      } else if (sourceIframe) {
        const modalIframe = sourceIframe.cloneNode(false);
        const sourceUrl = sourceIframe.dataset.src || sourceIframe.src;
        modalIframe.className = 'hy-reels-modal__iframe';
        modalIframe.removeAttribute('data-src');
        modalIframe.setAttribute('allow', 'autoplay; fullscreen');
        modalIframe.setAttribute('allowfullscreen', '');

        try {
          const playerUrl = new URL(sourceUrl, window.location.href);
          playerUrl.searchParams.set('autoplay', '1');
          playerUrl.searchParams.set('muted', '1');
          playerUrl.searchParams.set('controls', '1');
          modalIframe.src = playerUrl.toString();
        } catch (error) {
          modalIframe.src = sourceUrl;
        }

        this.modalStage.appendChild(modalIframe);
      }

      const hasMultipleVideos = this.cards.length > 1;
      if (this.previousButton) this.previousButton.hidden = !hasMultipleVideos;
      if (this.nextButton) this.nextButton.hidden = !hasMultipleVideos;
    }

    pauseModalMedia() {
      if (!this.modalStage) return;

      const video = this.modalStage.querySelector('video');
      const iframe = this.modalStage.querySelector('iframe');

      if (video && !video.paused) video.pause();
      if (iframe && iframe.src) this.messageTikTok(iframe, 'pause');
    }

    isCardVisible(card, minimumRatio = 0.15) {
      if (!card || !card.isConnected) return false;

      const rect = card.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return false;

      const visibleWidth = Math.max(
        0,
        Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0),
      );
      const visibleHeight = Math.max(
        0,
        Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0),
      );
      const visibleRatio = (visibleWidth * visibleHeight) / (rect.width * rect.height);

      return visibleRatio >= minimumRatio;
    }

    resumeCardsAfterModal() {
      const cardsToResume = Array.from(this.cardsToResumeAfterModal || []);
      this.cardsToResumeAfterModal = new Set();

      if (!this.isConnected || document.hidden || cardsToResume.length === 0) return;

      window.requestAnimationFrame(() => {
        cardsToResume.forEach((card) => {
          if (!this.isCardVisible(card)) return;
          this.loadTikTok(card);
          this.playCard(card);
        });
      });
    }

    trapModalFocus(event) {
      if (!this.modalDialog) return;

      const focusable = Array.from(
        this.modalDialog.querySelectorAll(
          'button:not([disabled]):not([hidden]), a[href], video[controls], iframe, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => !element.hasAttribute('hidden'));

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    submitProductForm() {
      const section = this.closest('.shopify-section') || this.closest('section');
      const productForm = section && section.querySelector('product-form form[action*="/cart/add"]');
      const submitButton = productForm && productForm.querySelector('[type="submit"]');

      if (!productForm || !submitButton) return;

      if (this.addButton) {
        this.addButton.disabled = true;
        this.addButton.textContent = this.addButton.dataset.addedText || 'Added';
      }

      this.closeModal(false);

      if (typeof productForm.requestSubmit === 'function') {
        productForm.requestSubmit(submitButton);
      } else {
        submitButton.click();
      }

      window.setTimeout(() => {
        if (!this.addButton) return;
        this.addButton.disabled = false;
        this.addButton.textContent = this.addButton.dataset.defaultText || 'Add to cart';
      }, 1600);
    }

    loadTikTok(card) {
      const iframe = card.querySelector('iframe[data-src]');
      if (!iframe || iframe.hasAttribute('src')) return;
      iframe.src = iframe.dataset.src;
    }

    playCard(card) {
      if (this.modalOpen) return;

      const video = card.querySelector('video');
      const iframe = card.querySelector('iframe');

      if (video) {
        const promise = video.play();
        if (promise && typeof promise.catch === 'function') promise.catch(() => {});
      }

      if (iframe && iframe.src) this.messageTikTok(iframe, 'play');
    }

    pauseCard(card) {
      const video = card.querySelector('video');
      const iframe = card.querySelector('iframe');

      if (video && !video.paused) video.pause();
      if (iframe && iframe.src) this.messageTikTok(iframe, 'pause');
    }

    messageTikTok(iframe, type) {
      if (!iframe.contentWindow) return;

      iframe.contentWindow.postMessage(
        {
          type,
          value: null,
          'x-tiktok-player': true,
        },
        'https://www.tiktok.com',
      );
    }
  }

  customElements.define('hy-product-reels', HyProductReels);
})();
